<template>
  <div>
    <base-header class="pb-6"></base-header>
    <div class="container-fluid mt--6">
      <div class="card">
        <div class="card-header border-0">
          <h3 class="mb-0">Lista de Insumos Foodcourt</h3>
          <p class="text-sm text-muted mb-0">
            Proyección de compra por rango de días. Es informativa: no genera órdenes ni
            se integra con proveedores.
          </p>
        </div>

        <div v-if="isLoading" class="text-center text-muted py-5">Cargando forecast…</div>
        <div v-else-if="errorMessage" class="text-center text-danger py-5">
          No se pudo cargar el forecast. {{ errorMessage }}
        </div>

        <template v-else-if="forecast && selectedKitchens.length">
          <div v-if="isInternalRole" class="card-header border-0 pt-0">
            <label class="form-control-label">Cocina</label>
            <el-select v-model="kitchenId" class="w-100 w-md-50" filterable>
              <el-option :value="null" label="Consolidado · todas las cocinas"></el-option>
              <el-option
                v-for="kitchen in kitchensByVolume"
                :key="kitchen.id"
                :label="kitchen.name + ' · ' + kitchen.country"
                :value="kitchen.id"
              ></el-option>
            </el-select>
          </div>

          <confidence-notices
            :kitchens="selectedKitchens"
            purchase-warning
          ></confidence-notices>

          <foodcourt-supply-filters
            :start-date="startDate"
            :end-date="endDate"
            :days="days"
            :channel="channel"
            :channels="forecast.channels"
            :category="category"
            :categories="supplyCategories"
            @update:startDate="startDate = $event"
            @update:days="days = $event"
            @update:channel="channel = $event"
            @update:category="category = $event"
          ></foodcourt-supply-filters>

          <div class="row starter-page">
            <div class="container-fluid">
              <el-table
                class="table-responsive table-flush"
                header-row-class-name="thead-light"
                :data="[requestedRange]"
              >
                <el-table-column label="Rango" min-width="220px">
                  <template v-slot="{row}">
                    del {{ row.startDate }} al {{ row.endDate }}
                    <div class="text-xs text-muted">{{ row.days }} días</div>
                  </template>
                </el-table-column>
                <el-table-column label="Cocina" min-width="200px">
                  <template>{{ kitchenLabel }}</template>
                </el-table-column>
                <el-table-column label="Canal" min-width="110px">
                  <template>{{ channelLabel(channel) }}</template>
                </el-table-column>
                <el-table-column label="Categoría" min-width="150px">
                  <template>{{ category || 'Todas' }}</template>
                </el-table-column>
                <el-table-column label="Reporte" min-width="200px">
                  <template>
                    <base-button type="danger" :loading="isDownloading" @click="downloadPdf">
                      <i class="far fa-file-pdf"></i> Descargar PDF
                    </base-button>
                  </template>
                </el-table-column>
              </el-table>

              <div class="px-3 pb-3">
                <div class="text-xs text-muted mt-2">
                  El PDF arranca con el <b>consolidado de todo lo que hay que comprar</b> y
                  sigue con el detalle por categoría, con subtotal. La cantidad es la que
                  hay que pedir: porción de la receta ÷ rendimiento ÷ (1 − merma) × platos
                  proyectados de cada día del rango.
                  <b>No hace falta regenerarlo a diario</b>: el error diario es plano entre
                  1 y 14 días, así que acercarse a la fecha no mejora la precisión.
                  Los nombres son los de la <b>receta</b>, no los de compra: hay que
                  traducirlos antes de mandarle el pedido al proveedor.
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import ConfidenceNotices from "@/components/ConfidenceNotices";
import FoodcourtSupplyFilters from "@/components/FoodcourtSupplyFilters";
import ForecastGateway from "@/gateways/ForecastGateway";
import { Table, TableColumn, Select, Option } from "element-ui";

const CHANNEL_LABELS = { propio: "Propio", apps: "App", todos: "Todos" };
const SUGGESTED_DAYS = 5;

export default {
  name: "lista-insumos-foodcourt",
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    ConfidenceNotices,
    FoodcourtSupplyFilters,
  },
  data() {
    return {
      isLoading: true,
      errorMessage: null,
      forecast: null,
      recipeSheets: null,
      kitchenId: null,
      channel: "todos",
      category: null,
      startDate: this.toIsoDate(new Date()),
      days: SUGGESTED_DAYS,
      isDownloading: false,
    };
  },
  async mounted() {
    try {
      this.forecast = await this.fetchForecast();
      if (this.kitchensByVolume.length) {
        this.kitchenId = this.kitchensByVolume[0].id;
      }
      this.recipeSheets = await this.fetchRecipeSheets();
    } catch (error) {
      this.errorMessage = this.apiMessage(error) || "error";
    } finally {
      this.isLoading = false;
    }
  },
  computed: {
    endDate() {
      const end = new Date(this.startDate + "T00:00:00");
      end.setDate(end.getDate() + this.days - 1);
      return this.toIsoDate(end);
    },
    requestedRange() {
      return { startDate: this.startDate, endDate: this.endDate, days: this.days };
    },
    kitchensByVolume() {
      return this.forecast
        ? this.forecast.kitchens.slice().sort((a, b) => b.weekOrders - a.weekOrders)
        : [];
    },
    isInternalRole() {
      return !!(this.forecast && this.forecast.scope && this.forecast.scope.internal);
    },
    selectedKitchens() {
      if (!this.forecast) return [];
      if (this.kitchenId === null || this.kitchenId === "") return this.kitchensByVolume;
      return this.kitchensByVolume.filter((kitchen) => kitchen.id === this.kitchenId);
    },
    kitchenLabel() {
      const selected = this.selectedKitchens;
      return selected.length === 1
        ? selected[0].name
        : `Consolidado · ${selected.length} cocinas`;
    },
    supplyCategories() {
      if (!this.recipeSheets) return [];
      const found = {};
      Object.keys(this.recipeSheets).forEach((dish) => {
        this.recipeSheets[dish].forEach((ingredient) => {
          if (ingredient.des) return;
          const category = (ingredient.cat || "").trim();
          if (category) found[category] = true;
        });
      });
      return Object.keys(found).sort((a, b) => a.localeCompare(b, "es"));
    },
  },
  methods: {
    toIsoDate(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(date.getDate()).padStart(2, "0")}`;
    },
    channelLabel(channel) {
      return CHANNEL_LABELS[channel] || channel;
    },
    resultOf(response) {
      return (
        (response.data && response.data.payload && response.data.payload.result) ||
        response.data
      );
    },
    apiMessage(error) {
      const payload =
        error && error.response && error.response.data && error.response.data.payload;
      return (payload && payload.message) || (error && error.message) || "";
    },
    async fetchForecast() {
      return this.resultOf(await ForecastGateway.obtener());
    },
    async fetchRecipeSheets() {
      try {
        const result = this.resultOf(await ForecastGateway.fichas());
        return (result && result.fichas) || null;
      } catch (error) {
        return null;
      }
    },
    async downloadPdf() {
      this.isDownloading = true;
      try {
        const response = await ForecastGateway.descargarInsumos(
          this.startDate,
          this.endDate,
          this.channel,
          this.kitchenId,
          this.category
        );
        this.saveBlob(response);
      } catch (error) {
        alert("No se pudo generar el archivo. " + (await this.blobMessage(error)));
      } finally {
        this.isDownloading = false;
      }
    },
    saveBlob(response) {
      const disposition = response.headers && response.headers["content-disposition"];
      const fromHeader = disposition && disposition.match(/filename="?([^";]+)"?/i);
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = fromHeader
        ? fromHeader[1]
        : `insumos-foodcourt-${this.startDate}-a-${this.endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    async blobMessage(error) {
      const data = error && error.response && error.response.data;
      if (!(data instanceof Blob)) {
        return this.apiMessage(error);
      }
      try {
        const parsed = JSON.parse(await data.text());
        return (parsed.payload && parsed.payload.message) || "";
      } catch (parseError) {
        return this.apiMessage(error);
      }
    },
  },
};
</script>
<style scoped>
.starter-page {
  min-height: calc(100vh - 380px);
}
</style>
